import styled, { createGlobalStyle } from 'styled-components';

export const theme = {
  colors: {
    // Primary cybersecurity palette
    primary: '#0f172a', // Deep navy/midnight blue
    secondary: '#1e293b', // Charcoal/dark gray
    accent: '#06b6d4', // Electric blue/teal
    accentHover: '#0891b2', // Darker teal for hover states
    
    // Background colors
    background: '#0f172a', // Deep navy background
    backgroundSecondary: '#1e293b', // Charcoal secondary background
    backgroundTertiary: '#334155', // Lighter charcoal for cards
    
    // Text colors
    text: '#f8fafc', // Soft white for primary text
    textSecondary: '#cbd5e1', // Light gray for secondary text
    textMuted: '#94a3b8', // Muted gray for tertiary text
    
    // Interactive elements
    light: '#ffffff',
    dark: '#0f172a',
    gray: '#64748b',
    lightGray: '#334155',
    
    // Status colors (muted for professional look)
    success: '#10b981', // Muted green
    warning: '#f59e0b', // Muted amber
    danger: '#ef4444', // Muted crimson
    info: '#3b82f6', // Professional blue
    
    // Border and divider colors
    border: '#334155',
    borderLight: '#475569',
    
    // Overlay colors
    overlay: 'rgba(15, 23, 42, 0.8)',
    overlayLight: 'rgba(15, 23, 42, 0.6)'
  },
  fonts: {
    main: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  },
  shadows: {
    small: '0 2px 4px rgba(15, 23, 42, 0.3)',
    medium: '0 4px 8px rgba(15, 23, 42, 0.4)',
    large: '0 8px 16px rgba(15, 23, 42, 0.5)',
    glow: '0 0 20px rgba(6, 182, 212, 0.3)',
    glowAccent: '0 0 30px rgba(6, 182, 212, 0.4)'
  },
  transitions: {
    fast: '0.2s ease',
    medium: '0.3s ease',
    slow: '0.5s ease'
  },
  gradients: {
    primary: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    accent: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    hero: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
    card: 'linear-gradient(145deg, #1e293b 0%, #334155 100%)'
  }
};

export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: ${theme.fonts.main};
    line-height: 1.6;
    color: ${theme.colors.text};
    background: ${theme.colors.background};
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  ul {
    list-style: none;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Custom scrollbar for dark theme */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.accent};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.accentHover};
  }

  /* Selection styling */
  ::selection {
    background: ${theme.colors.accent};
    color: ${theme.colors.text};
  }

  ::-moz-selection {
    background: ${theme.colors.accent};
    color: ${theme.colors.text};
  }

  /* Navbar Styles */
  .navbar-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: rgba(15, 23, 42, 0.98);
    backdrop-filter: blur(10px);
    box-shadow: ${theme.shadows.small};
    border-bottom: 1px solid ${theme.colors.border};
    transition: all ${theme.transitions.medium};
  }

  .navbar-container.scrolled {
    background: rgba(15, 23, 42, 0.95);
    box-shadow: ${theme.shadows.medium};
  }

  .navbar-container .dropdown-menu {
    z-index: 1001;
  }

  .nav-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    align-items: center;
    height: 80px;
    gap: 40px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    .nav-content {
      height: 70px;
      gap: 20px;
    }
  }

  .navbar-logo {
    font-size: 1.8rem;
    font-weight: 800;
    color: ${theme.colors.accent};
    text-decoration: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    .navbar-logo {
      font-size: 1.5rem;
    }
  }

  .nav-menu {
    display: flex;
    align-items: center;
    gap: 30px;
    margin-left: auto;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .nav-menu {
      position: fixed;
      top: 80px;
      left: 0;
      right: 0;
      background: ${theme.colors.backgroundSecondary};
      flex-direction: column;
      padding: 20px;
      box-shadow: ${theme.shadows.large};
      border: 1px solid ${theme.colors.border};
      transform: translateY(-100%);
      opacity: 0;
      visibility: hidden;
      transition: all ${theme.transitions.medium};
      max-height: 80vh;
      overflow-y: auto;
      gap: 15px;
      align-items: stretch;
      margin-left: 0;
    }

    .nav-menu.open {
      transform: translateY(0);
      opacity: 1;
      visibility: visible;
    }
  }

  .nav-item {
    position: relative;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .nav-item {
      width: 100%;
    }
  }

  .nav-item:hover .dropdown-menu {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .nav-item .dropdown-menu:hover {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .nav-link {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 10px 15px;
    color: ${theme.colors.text};
    font-weight: 500;
    font-size: 16px;
    transition: all ${theme.transitions.fast};
    border-radius: 5px;
    text-decoration: none;
  }

  .nav-link.active {
    color: ${theme.colors.accent};
    font-weight: 600;
  }

  .nav-link:hover {
    color: ${theme.colors.accent};
    background: ${theme.colors.backgroundTertiary};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .nav-link {
      width: 100%;
      justify-content: space-between;
      padding: 15px 20px;
      border-bottom: 1px solid ${theme.colors.border};
    }
  }

  .nav-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 10px 15px;
    color: ${theme.colors.text};
    font-weight: 500;
    font-size: 16px;
    transition: all ${theme.transitions.fast};
    border-radius: 5px;
    background: none;
    border: none;
    cursor: pointer;
    width: 100%;
    text-align: center;
  }

  .nav-button .dropdown-arrow {
    opacity: 0;
    transform: translateY(-5px) scale(0.8);
    transition: all ${theme.transitions.fast};
  }

  .nav-button.dropdown-open .dropdown-arrow {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .nav-button:hover {
    color: ${theme.colors.accent};
    background: ${theme.colors.backgroundTertiary};
  }

  .nav-button:hover .dropdown-arrow {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .nav-button {
      flex-direction: row;
      justify-content: space-between;
      padding: 15px 20px;
      border-bottom: 1px solid ${theme.colors.border};
      text-align: left;
      align-items: center;
    }

    .nav-button .dropdown-arrow {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    min-width: 250px;
    background: ${theme.colors.backgroundSecondary};
    box-shadow: ${theme.shadows.large};
    border: 1px solid ${theme.colors.border};
    border-radius: 8px;
    padding: 8px 0;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all ${theme.transitions.fast};
    z-index: 1000;
    white-space: nowrap;
    list-style: none;
  }

  .dropdown-menu.open {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .dropdown-menu.dropdown-right {
    right: 0;
    left: auto;
  }

  .dropdown-menu.dropdown-center {
    left: 50%;
    transform: translateX(-50%) translateY(-10px);
  }

  .dropdown-menu.dropdown-center.open {
    transform: translateX(-50%) translateY(0);
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .dropdown-menu {
      position: static;
      box-shadow: none;
      background: ${theme.colors.backgroundTertiary};
      border-radius: 0;
      margin-top: 10px;
      padding: 0;
      opacity: 1;
      visibility: visible;
      transform: none;
      display: none;
      min-width: auto;
      width: 100%;
      white-space: normal;
    }

    .dropdown-menu.open {
      display: block;
    }
  }

  .dropdown-item {
    list-style: none;
  }

  .dropdown-item:hover {
    background: ${theme.colors.backgroundTertiary};
  }

  .dropdown-link {
    display: block;
    padding: 12px 20px;
    color: ${theme.colors.text};
    font-weight: 400;
    transition: all ${theme.transitions.fast};
    text-decoration: none;
  }

  .dropdown-link:hover {
    color: ${theme.colors.accent};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .dropdown-link {
      padding: 12px 30px;
      border-bottom: 1px solid ${theme.colors.border};
    }
  }

  .mobile-menu-button {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: ${theme.colors.text};
    cursor: pointer;
    margin-left: auto;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .mobile-menu-button {
      display: block;
    }
  }

  .sub-dropdown-menu {
    left: calc(100% + 8px);
    top: 0;
    min-width: 220px;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    .sub-dropdown-menu {
      left: 0;
      top: auto;
      background: ${theme.colors.background};
      min-width: auto;
      width: 100%;
    }
  }
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 15px;
  }
`;

export const Section = styled.section`
  padding: 80px 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

export const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' | 'ghost' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  transition: all ${theme.transitions.fast};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left ${theme.transitions.medium};
  }

  &:hover::before {
    left: 100%;
  }

  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'primary':
        return `
          background: ${theme.gradients.accent};
          color: ${theme.colors.text};
          box-shadow: ${theme.shadows.glow};
          &:hover {
            background: ${theme.colors.accentHover};
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.glowAccent};
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.border};
          &:hover {
            background: ${theme.colors.accent};
            border-color: ${theme.colors.accent};
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.glow};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.accent};
          border: 2px solid ${theme.colors.accent};
          &:hover {
            background: ${theme.colors.accent};
            color: ${theme.colors.text};
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.glow};
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover {
            background: ${theme.colors.backgroundTertiary};
            color: ${theme.colors.text};
            transform: translateY(-1px);
          }
        `;
    }
  }}
`;

export const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: 20px;
  text-align: center;
  background: ${theme.gradients.accent};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.textSecondary};
  text-align: center;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.6;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

